<template>
  <div class="top-bar-actions" data-testid="top-bar-actions">
    <!-- Favorite -->
    <button
      data-testid="viewer-topbar-favorite"
      class="top-bar-actions__icon-btn"
      :title="favorite ? 'Remove from Favorites' : 'Add to Favorites'"
      :aria-label="favorite ? 'Remove from Favorites' : 'Add to Favorites'"
      @click="onFavoriteClick"
    >
      <Icon
        :name="favoriteIcon"
        size="base"
        :class="favorite ? 'text-copernicus-yellow' : ''"
        @mouseover="favoriteHovered = true"
        @mouseleave="favoriteHovered = false"
      />
    </button>

    <!-- Add to Collection -->
    <button
      data-testid="viewer-topbar-collection"
      class="top-bar-actions__icon-btn"
      title="Add to Collection"
      aria-label="Add to Collection"
      @click="addToCollectionAction"
    >
      <Icon :name="collectionIcon" size="base" />
    </button>

    <!-- Download -->
    <button
      data-testid="viewer-topbar-download"
      class="top-bar-actions__icon-btn"
      :title="isEditProcessing ? 'Processing' : 'Download'"
      :aria-label="isEditProcessing ? 'Processing' : 'Download'"
      :disabled="isEditProcessing"
      @click="onDownloadClick"
    >
      <Icon name="general.download" size="base" />
    </button>

    <!-- More (three dots) -->
    <ContextMenuWrapper placement="bottom-end" test-id="viewer-topbar-more" class="top-bar-actions__more-menu">
      <button data-testid="viewer-topbar-more-trigger" class="top-bar-actions__icon-btn" aria-label="More actions">
        <Icon name="general.meatballMoreDots" size="base" />
      </button>
      <template #menu>
        <template v-for="(item, index) in moreMenuItems" :key="item.name || `sep-${index}`">
          <MenuSeparator v-if="item.isSeparator" />
          <MenuToggle v-else-if="item.isToggle" :title="item.title" :type="item.type" v-model="item.value" @click.prevent="runAction(item)">
            {{ item.title }}
          </MenuToggle>
          <MenuLink
            v-else
            :icon="item.icon"
            :title="item.title"
            :type="item.type"
            :disabled="item.disabled"
            :loading="item.loading"
            @click.prevent="runAction(item)"
          >
            {{ item.title }}
          </MenuLink>
        </template>
      </template>
    </ContextMenuWrapper>

    <!-- Share Button -->
    <Button type="solid" variant="blue" :icon="getSharingIcon()" data-testid="viewer-topbar-share" @click.prevent="openShareModalAction">
      <span class="hidden sm:inline">Share</span>
    </Button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { Icon, Button } from '@/storybook';
import { ContextMenuWrapper, MenuSeparator, MenuLink, MenuToggle } from '@/storybook/menu';

export interface ToolbarMenuItem {
  name?: string;
  title?: string;
  icon?: string;
  type?: string;
  href?: string;
  isSeparator?: boolean;
  isToggle?: boolean;
  disabled?: boolean;
  loading?: boolean;
  value?: boolean;
  action?: () => void;
}

import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useCollectionsStore } from '@/store/useCollectionsStore';
import { useItemActions } from '@/modules/viewer/composables/useItemActions';
import { useItemActionsToolbar } from '@/modules/viewer/composables/useItemActionsToolbar';
import { useItemShare } from '@/modules/viewer/composables/useItemShare';
import { TrackEvents, useViewerTrack } from '@/modules/viewer/composables/useViewerTrack';

const viewerItemStore = useViewerItemStore();
const { id, favorite, isEditProcessing } = storeToRefs(viewerItemStore);
const { list } = storeToRefs(useCollectionsStore());
const { trackItemOwner } = useViewerTrack();

const { downloadAction, addToCollectionAction, openShareModalAction } = useItemActions();
const { itemActionsMenu } = useItemActionsToolbar();
const { getSharingIcon } = useItemShare();

const favoriteHovered = ref(false);
const isSavingFavorite = ref(false);

const favoriteIcon = computed(() => {
  if (!favorite.value) {
    return favoriteHovered.value ? 'general.favoritesSolid' : 'general.favorites';
  }
  return 'general.favoritesSolid';
});

const hasCollection = computed(() => {
  if (id.value === undefined) {
    return false;
  }
  return list.value ? list.value.filter(col => col.items.includes(id.value || '')).length > 0 : false;
});

const collectionIcon = computed(() => (hasCollection.value ? 'general.folderFull' : 'general.folder'));

const moreMenuItems = computed(() => {
  if (!itemActionsMenu.value) {
    return [];
  }
  const filtered = itemActionsMenu.value.filter(
    (item: ToolbarMenuItem) => !['favorites-add', 'favorites-remove', 'collection', 'download'].includes(item.name || '')
  );

  return filtered.filter((item: ToolbarMenuItem, index: number, arr: ToolbarMenuItem[]) => {
    if (!item.isSeparator) {
      return true;
    }
    const isFirst = index === 0;
    const isLast = index === arr.length - 1;
    const prevIsSeparator = index > 0 && arr[index - 1].isSeparator;
    return !isFirst && !isLast && !prevIsSeparator;
  });
});

const onFavoriteClick = async () => {
  if (isSavingFavorite.value) {
    return;
  }
  trackItemOwner(TrackEvents.ITEM_ADD_TO_FAVORITES);
  isSavingFavorite.value = true;
  try {
    await viewerItemStore.toggleFavorite(id.value);
  } finally {
    isSavingFavorite.value = false;
  }
};

const onDownloadClick = async () => {
  if (isEditProcessing.value) {
    return;
  }
  await downloadAction();
};

const runAction = (item: ToolbarMenuItem) => {
  if (typeof item?.action === 'function') {
    return item.action();
  }
};
</script>

<style scoped>
.top-bar-actions {
  @apply flex items-center gap-1 sm:gap-2;
}

.top-bar-actions__icon-btn {
  @apply p-2 rounded-md text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed;
  min-width: 40px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.top-bar-actions__more-menu :deep(.context-menu) {
  max-height: calc(100dvh - 6rem);
  overflow-y: auto;
}
</style>
